<template>
  <component
    :is="isLinked ? 'a' : 'article'"
    :href="isLinked ? note.url : undefined"
    :target="isLinked ? target : undefined"
    :rel="isLinked ? 'nofollow noindex' : undefined"
    :class="[`ag-block--note-simple5--has-${note.image_display}-image`, { 'ag-block--note-simple5--linked': isLinked }]"
    @click="onBlockClick"
  >
    <Image
      :media="note.featured_image"
      :alt="note.title"
      lazy
      class="ag-block__featured-image"
    />

    <span
      v-if="note.image_display === 'full'"
      class="ag-block__full-image"
    >
      <Image
        :media="note.featured_image"
        :alt="note.title"
        lazy
      />
    </span>

    <div class="ag-block__body">
      <div class="ag-block__body-inner">
        <Markdown
          v-if="note.title"
          as="h3"
          class="ag-block__title"
          :text="note.title"
          :should-strip-links="isLinked"
        />
        <Markdown
          v-if="note.synopsis"
          as="div"
          class="ag-block__description"
          :text="note.synopsis"
          :should-strip-links="isLinked"
        />
        <div
          v-if="note.url && note.call_to_action_text"
          class="ag-block__cta"
        >
          <Button
            :href="note.url"
            :target="target"
            as="a"
            class="ag-block__button"
            rel="nofollow noindex"
            @click.prevent="onClick(block)"
          >
            <span>{{ note.call_to_action_text }}</span>
          </Button>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import type { NoteBlock } from '~/types/blocks'

const props = defineProps<{
  block: NoteBlock
}>()

const note = props.block.item

const target = note?.url_type === 'link' ? '_blank' : undefined

// Without CTA text the URL links the entire block, so inner links are stripped to avoid nesting them
const isLinked = !!note.url && !note.call_to_action_text

const { onClick } = useBlock()

const onBlockClick = (event: MouseEvent) => {
  if (!isLinked) return

  event.preventDefault()
  onClick(props.block)
}
</script>

<style>
.ag-block--note-simple5 {
  @apply flex flex-col overflow-hidden rounded-lg bg-slate-900;

  .ag-block__featured-image {
    @apply absolute inset-0 h-full w-full object-cover transition;
  }

  .ag-block__full-image {
    @apply relative z-20 m-4 mb-0 overflow-hidden rounded-lg;

    img {
      @apply transition;
    }
  }

  .ag-block__body {
    @apply relative w-full p-8;

    &-inner {
      @apply relative z-20;
    }
  }

  .ag-block__title,
  .ag-block__title a {
    @apply text-white;
  }

  .ag-block__description {
    @apply text-white;
  }

  &--linked {
    @apply cursor-pointer focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-500 focus-visible:ring-offset-2;

    &:hover {
      .ag-block__featured-image,
      .ag-block__full-image img {
        @apply scale-110;
      }
    }
  }

  &--has-cover-image {
    @apply justify-end pt-60;

    .ag-block__body {
      &::before {
        @apply absolute inset-0 bg-gradient-to-t from-slate-900;
        content: '';
      }
    }
  }

  &--has-full-image {
    @apply justify-between;

    &::before,
    &::after {
      @apply absolute inset-0 z-10;
      content: '';
    }

    &::before {
      @apply backdrop-blur-sm;
    }

    &::after {
      @apply bg-gradient-to-t from-slate-900;
    }
  }
}
</style>
